import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ChevronDown, Minus, Plus, ScanFace, Scissors, ShieldCheck, Truck } from "lucide-react";
import { api, fmtEUR, fmtXOF } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useTryOn } from "../context/TryOnContext";
import { Reveal } from "../components/Reveal";
import { sizeLabel } from "../lib/sizeLabel";

const Accordion = ({ title, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-ligne">
      <button
        data-testid={`accordion-${title.toLowerCase().replace(/\s/g, "-")}`}
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between py-5 text-start font-mono text-[11px] uppercase tracking-[0.25em] text-ivoire transition-colors hover:text-or"
      >
        {title}
        <ChevronDown size={15} className={`text-or transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="pb-6 text-sm leading-relaxed text-argile">{children}</div>}
    </div>
  );
};

export default function Produit() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(false);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const { addItem, openCart } = useCart();
  const { toggle: tryonToggle } = useTryOn();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${slug}`)
      .then((r) => { setProduct(r.data); setSize(r.data.sizes?.[0] || "Unique"); })
      .catch(() => setError(true));
  }, [slug, i18n.language]);

  if (error)
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-6 px-6 text-center">
        <p className="font-serif text-3xl text-ivoire">{t("product.notFound")}</p>
        <Link data-testid="product-not-found-back" to="/boutique" className="border border-or/60 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-or hover:bg-or hover:text-noir">
          {t("common.backToShop")}
        </Link>
      </main>
    );

  if (!product)
    return <main className="min-h-screen pt-40 text-center font-mono text-[11px] uppercase tracking-[0.3em] text-argile">{t("common.loading")}</main>;

  const add = () => {
    addItem(product, size || "Unique", qty);
    toast.success(t("common.added", { name: product.name }));
    openCart();
  };

  return (
    <main data-testid="product-page" className="mx-auto max-w-[1500px] px-6 pb-24 pt-32 lg:px-12 lg:pt-40">
      <Link data-testid="product-back-link" to="/boutique" className="font-mono text-[10px] uppercase tracking-[0.25em] text-argile transition-colors hover:text-or">
        ← {t("product.backLink")}
      </Link>
      <div className="mt-8 grid gap-14 lg:grid-cols-2">
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="lg:sticky lg:top-28 lg:self-start">
          <div className="gold-frame frame-inner group relative overflow-hidden border border-ligne">
            <img
              src={product.image}
              alt={product.name}
              className="aspect-[3/4] w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
            />
          </div>
        </motion.div>

        <Reveal y={16}>
          {product.badge && <span className="inline-block border border-or/40 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em] text-or">{product.badge}</span>}
          <h1 className="mt-5 font-serif text-4xl uppercase tracking-tight text-ivoire sm:text-5xl">{product.name}</h1>
          <div className="mt-5 flex items-baseline gap-4" data-testid="product-price">
            <p className="font-serif text-3xl text-or">{fmtXOF(product.price_xof)}</p>
            <p className="text-sm text-argile">{fmtEUR(product.price_xof)}</p>
          </div>
          <p className="mt-8 max-w-lg leading-relaxed text-argile">{product.description}</p>

          <div className="mt-10">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-argile">{t("common.size")}</p>
            <div className="mt-4 flex flex-wrap gap-3">
              {product.sizes.map((s) => (
                <button
                  key={s}
                  data-testid={`size-option-${s.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => setSize(s)}
                  className={`min-w-[3.5rem] border px-4 py-3 font-mono text-[11px] tracking-[0.15em] transition-all duration-300 ${
                    size === s ? "border-or bg-or text-noir" : "border-ligne text-argile hover:border-or hover:text-or"
                  }`}
                >
                  {sizeLabel(s, t)}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-10 flex flex-wrap items-stretch gap-4">
            <div className="flex items-center border border-ligne">
              <button data-testid="product-qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-4 text-argile hover:text-or" aria-label={t("product.qtyMinus")}><Minus size={14} /></button>
              <span data-testid="product-qty" className="w-10 text-center font-mono text-sm text-ivoire">{qty}</span>
              <button data-testid="product-qty-plus" onClick={() => setQty(Math.min(10, qty + 1))} className="px-4 py-4 text-argile hover:text-or" aria-label={t("product.qtyPlus")}><Plus size={14} /></button>
            </div>
            <button
              data-testid="add-to-cart-button"
              onClick={add}
              className="flex-1 bg-or px-8 py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-noir transition-all duration-300 hover:bg-orclair"
            >
              {t("product.addToCart", { price: fmtXOF(product.price_xof * qty) })}
            </button>
          </div>
          <button
            data-testid="product-tryon-button"
            onClick={() => {
              tryonToggle(product);
              toast.success(t("tryon.tried", { name: product.name }));
              navigate("/essayage");
            }}
            className="mt-4 flex w-full items-center justify-center gap-3 border border-or/60 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-or transition-all duration-300 hover:bg-or hover:text-noir"
          >
            <ScanFace size={14} /> {t("tryon.titlePre")} {t("tryon.titleIt")}
          </button>

          <div className="mt-10 space-y-4">
            {[
              { icon: Truck, text: t("product.info1") },
              { icon: ShieldCheck, text: t("product.info2") },
              { icon: Scissors, text: t("product.info3") },
            ].map(({ icon: Icon, text }, i) => (
              <div key={i} className="flex items-center gap-4 border border-ligne bg-card2 px-5 py-4">
                <Icon size={17} className="shrink-0 text-or" strokeWidth={1.5} />
                <p className="text-xs leading-relaxed text-argile">{text}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 border-t border-ligne">
            <Accordion title={t("product.craft")}>
              {t("product.craftText", { fabric: product.fabric })}
            </Accordion>
            <Accordion title={t("product.customAcc")}>
              {t("product.customText")}
            </Accordion>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
